import isNotEmptyArray from '@travel/ui/utils/arrays';
import { ProviderListSort, PlanListSort } from './favoriteLists';
import type { Query } from 'ProviderPlan-Types';
import { GradeMemberRank } from 'favoriteList/constants/favoriteLists';

// SORT
export type SortOptionProvider = {
  [key in keyof typeof ProviderListSort]: { labelId: string; value: ProviderListSort };
};
export type SortOptionPlan = {
  [key in keyof typeof PlanListSort]: { labelId: string; value: PlanListSort };
};
export const SORT_OPTIONS_PLAN: SortOptionPlan = {
  LATEST: {
    labelId: '最近追加した順',
    value: PlanListSort.LATEST,
  },
  PRICE_LOW: {
    labelId: '料金の安い順',
    value: PlanListSort.PRICE_LOW,
  },
  PRICE_HIGH: {
    labelId: '料金の高い順',
    value: PlanListSort.PRICE_HIGH,
  },
};

export const SORT_OPTIONS_PROVIDER: SortOptionProvider = {
  LATEST: {
    labelId: '最近追加した順',
    value: ProviderListSort.LATEST,
  },
  PRICE_LOW: {
    labelId: '料金の安い順',
    value: ProviderListSort.PRICE_LOW,
  },
  PRICE_HIGH: {
    labelId: '料金の高い順',
    value: ProviderListSort.PRICE_HIGH,
  },
  RATED_HIGH: {
    labelId: '評価が高い順',
    value: ProviderListSort.RATED_HIGH,
  },
};

// FILTER
export type Filter = {
  featureGroup: string;
  featureGroupId: number;
  noInitialDisp: number | null;
  features?: {
    nfeature: string;
    ksearchValue: string;
    ksearchKey: string;
    noCutShortOver: number | null;
    secondFeatures?: {
      nfeature: string;
      ksearchValue: string;
      ksearchKey: string;
      noCutShortOver: number | null;
    }[];
  }[];
};
export type Group = {
  groupId: string;
  groupName: string;
  noInitialDisp: number | null;
  features?: Array<Feature>;
};
export type Feature = {
  featureId: string;
  name: string;
  searchKey?: string;
  selected?: boolean;
  className?: string;
  iconClassName?: string;
  noCutShortOver: number | null;
  secondFeatures?: {
    featureId: string;
    name: string;
    searchKey?: string;
    selected?: boolean;
    className?: string;
    iconClassName?: string;
    noCutShortOver: number | null;
  }[];
};

// FUNCTIONS
export const getMemberRank = () => {
  // Check if the code is running in the browser
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const diamondElements = document.getElementsByClassName('rank diamond');
    const platinumElements = document.getElementsByClassName('rank platinum');

    if (diamondElements.length > 0) {
      return GradeMemberRank.DIAMOND_RANK_MEMBER;
    } else if (platinumElements.length > 0) {
      return GradeMemberRank.PLATINUM_RANK_MEMBER;
    } else {
      return '';
    }
  }

  // Return a default value during SSR
  return '';
};

export const removeFeatureGroup = (data: Filter[], groupId: string) => {
  return data?.filter((group) => group.featureGroup !== groupId);
};

export const replaceFeatureName = (data: Filter[], oldName: string, newName: string) => {
  return data?.map((group) => {
    return {
      ...group,
      features: group.features?.map((feature) => {
        if (feature.nfeature === oldName) {
          return { ...feature, nfeature: newName };
        } else {
          return feature;
        }
      }),
    };
  });
};

export const shortenText = (text: string, textLength?: number | null) => {
  if (textLength && text.length > textLength) {
    return text.slice(0, textLength) + '...';
  } else {
    return text;
  }
};

export const getFilterObject = (query?: Query) => {
  const filters = query?.f_squeezes as string | string[];
  const isParent = query?.isParent as string | string[];
  const searchKey = 'f_squeezes';

  // Convert isParent to an array if it's a string
  const isParentArray = typeof isParent === 'string' ? [isParent] : isParent || [];

  // Convert filters to an array if it's a string
  const filtersArray = typeof filters === 'string' ? [filters] : filters || [];

  // Map the filters to the desired format with the isParent flag
  return filtersArray.map((featureId) => ({
    searchKey,
    featureId,
    isParent: isParentArray.includes(featureId),
  }));
};

export const getSelectedFeatures = (filters: Group[]) => {
  const selectedFeatures = [] as Feature[];
  filters?.forEach((group) =>
    group.features?.forEach((feature) => {
      // only push non-second feature to array, PRD required
      if (feature.selected && !isNotEmptyArray(feature.secondFeatures)) {
        selectedFeatures.push(feature);
      }
      feature.secondFeatures?.forEach((secFeature) => {
        if (secFeature.selected) {
          selectedFeatures.push(secFeature);
        }
      });
    }),
  );

  const uniqueSelectedFeatures = Array.from(
    new Map(selectedFeatures.map((feature) => [feature.featureId, feature])).values(),
  );

  return {
    selectedFeatures: selectedFeatures,
    uniqueSelectedFeatures: uniqueSelectedFeatures,
  };
};
export const getSelectedFeatureIds = (filters: Group[]) => {
  const selectedFeatureIds = [] as { searchKey?: string; featureId: string; isParent?: boolean }[];

  filters.forEach((group) => {
    group.features?.forEach((feature) => {
      if (feature.selected) {
        selectedFeatureIds.push({
          searchKey: feature.searchKey,
          featureId: feature.featureId,
          isParent: isNotEmptyArray(feature.secondFeatures),
        });
      }
      feature.secondFeatures?.forEach((secFeature) => {
        if (secFeature.selected) {
          selectedFeatureIds.push({
            searchKey: secFeature.searchKey,
            featureId: secFeature.featureId,
          });
        }
      });
    });
  });

  return selectedFeatureIds;
};
export const getQueryParams = (search?: string) => {
  if (!search) return {};
  const params = new URLSearchParams(search);
  const queryParams = {};

  for (const [key, value] of params.entries()) {
    if (!queryParams[key]) {
      queryParams[key] = [];
    }
    queryParams[key].push(value);
  }

  return queryParams;
};
export const getQueryString = (
  sortKey: string,
  filters: { searchKey?: string; featureId: string; isParent?: boolean }[],
  offset?: number,
) => {
  const queryParams = {};

  // Add pagination
  if (offset || offset === 0) {
    queryParams['offset'] = offset;
  }

  // Add the sortKey to the query parameters
  if (sortKey) {
    queryParams['f_sort'] = sortKey;
  }

  // Add the filters to the query parameters
  filters.forEach((filter) => {
    if (filter.searchKey && filter.featureId) {
      if (!queryParams[filter.searchKey]) {
        queryParams[filter.searchKey] = [];
      }
      queryParams[filter.searchKey].push(filter.featureId);
    }

    // Add isParent to the query parameters
    if (filter.isParent) {
      if (!queryParams['isParent']) {
        queryParams['isParent'] = [];
      }
      queryParams['isParent'].push(filter.featureId);
    }
  });

  // Construct the query string
  const queryString = Object.keys(queryParams)
    .map((key) => {
      if (Array.isArray(queryParams[key])) {
        return queryParams[key]
          .map((value) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`;
      }
    })
    .join('&');

  return queryString;
};
export const getInitFilters = (filters: Group[], search?: string) => {
  const queryParams = getQueryParams(search);
  const hasSearchKey = (featureId: string, searchKey?: string) =>
    searchKey && queryParams[searchKey]?.includes(featureId);

  return filters?.map((group) => {
    const newFeatures = group.features?.map((feature) => {
      const newSecondFeature = feature.secondFeatures?.map((secFeature) => {
        if (hasSearchKey(secFeature.featureId, secFeature.searchKey)) {
          return { ...secFeature, selected: true };
        } else {
          return secFeature;
        }
      });

      if (hasSearchKey(feature.featureId, feature.searchKey)) {
        return { ...feature, selected: true, secondFeatures: newSecondFeature };
      } else {
        return { ...feature, secondFeatures: newSecondFeature };
      }
    });

    return { ...group, features: newFeatures };
  });
};
export const transformData = (data: Filter[], className?: string, iconClassName?: string) => {
  return data?.map((group) => {
    // Create an array to hold the transformed features
    const transformedFeatures = [] as Feature[];

    // Iterate over each feature in the group
    group?.features?.forEach((feature) => {
      const secondFeature = feature.secondFeatures?.map((secFeature) => {
        return {
          name: secFeature.nfeature,
          featureId: secFeature.ksearchValue,
          searchKey: secFeature.ksearchKey,
          noCutShortOver: secFeature.noCutShortOver,
          className: className,
          iconClassName: iconClassName,
        };
      });

      // Add the original feature to the transformed features array
      transformedFeatures.push({
        name: feature.nfeature,
        featureId: feature.ksearchValue,
        searchKey: feature.ksearchKey,
        noCutShortOver: feature.noCutShortOver,
        secondFeatures: secondFeature,
        iconClassName: iconClassName,
      });
    });

    // Return the transformed group
    return {
      groupName: group.featureGroup,
      groupId: String(group.featureGroupId),
      noInitialDisp: group.noInitialDisp,
      features: transformedFeatures,
    };
  });
};
